/**
 * How long is left, said two different ways.
 *
 * Above a day the answer is coarse on purpose — "13 days" is what a manager wants in August and a
 * second-by-second clock is only stressful. Inside the last day it inverts: the number stops being
 * background and starts being the thing on the screen, and rounding it to "4 hours" hides whether
 * that is four hours or four hours fifty-nine. So the last day ticks.
 *
 * Kept out of the component because the boundary conditions are where this goes wrong — a clock
 * that shows `-1:59:59` after the deadline, or one that says `0 seconds` and never locks — and
 * those are worth testing rather than watching for.
 */

export interface Countdown {
  /** The big figure. `13`, or `4:07:22`. */
  num: string;
  /** What it counts. `days`, `left`, or empty once it has run out. */
  unit: string;
  /** Inside the final day, so the display should tick every second. */
  live: boolean;
  /** The moment has passed. Nothing may be written. */
  passed: boolean;
  /** Milliseconds until the next change worth re-rendering for, or null when it never changes. */
  tickMs: number | null;
}

const SEC = 1000;
const MIN = 60 * SEC;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

const pad = (n: number) => String(n).padStart(2, '0');

export function countdownTo(deadline: string | null | undefined, now: number): Countdown {
  if (!deadline) return { num: '', unit: '', live: false, passed: false, tickMs: null };
  const at = new Date(deadline).getTime();
  if (Number.isNaN(at)) return { num: '', unit: '', live: false, passed: false, tickMs: null };

  const left = at - now;
  if (left <= 0) return { num: '0', unit: '', live: false, passed: true, tickMs: null };

  if (left >= DAY) {
    /*
     * Ceiling, not floor. With 13 days and one minute to go a manager has fourteen calendar
     * touches on it, and flooring to 13 tells him he has less time than he does. It also means
     * the figure never sits on "0 days" while the deadline is still in the future — the last day
     * is the live clock, and there is no gap between the two.
     */
    const days = Math.ceil(left / DAY);
    return {
      num: String(days),
      unit: days === 1 ? 'day' : 'days',
      live: false,
      passed: false,
      // Re-render when the day count would change, and never more than once a minute.
      tickMs: Math.min(MIN, ((left - DAY) % DAY) + SEC),
    };
  }

  const h = Math.floor(left / HOUR);
  const m = Math.floor((left % HOUR) / MIN);
  const s = Math.floor((left % MIN) / SEC);
  return {
    num: `${h}:${pad(m)}:${pad(s)}`,
    unit: 'left',
    live: true,
    passed: false,
    tickMs: SEC,
  };
}
